import {
  endOfDay as endOfDayDateFns,
  endOfWeek,
  format,
  formatDistance,
  FormatDistanceFn,
  FormatDistanceFnOptions,
  formatDistanceStrict,
  FormatDistanceToken,
  formatDistanceToNow,
  FormatDistanceToNowOptions,
  formatRelative,
  getMonth,
  getWeek,
  getYear,
  isSameDay as isSameDayDateFns,
  parse,
  setWeek,
  startOfDay as startOfDayDateFns,
  startOfWeek,
} from "date-fns"
import { enUS, es } from "date-fns/locale"
import i18next from "i18next"

export const GENESIS_DATE = new Date("2025-04-01")

const locales = {
  "en-US": {
    ...enUS,
    formatDistance: ((
      token: FormatDistanceToken,
      count: number,
      options: FormatDistanceFnOptions,
    ) => {
      if (token === "aboutXHours" || token === "xHours") {
        return count === 1 ? "1 hour" : `${count} hours`
      }
      return enUS.formatDistance(token, count, options)
    }) as FormatDistanceFn,
  },
  "es-ES": {
    ...es,
    formatDistance: ((
      token: FormatDistanceToken,
      count: number,
      options: FormatDistanceFnOptions,
    ) => {
      if (token === "aboutXHours" || token === "xHours") {
        return count === 1 ? "1 hora" : `${count} horas`
      }
      return es.formatDistance(token, count, options)
    }) as FormatDistanceFn,
  },
}

export type DateFormat =
  | "P" // 7/10/2021
  | "PP" // Jul 10, 2021
  | "p" // 12:34 AM
  | "Pp" // 7/10/2021 12:34 AM
  | "yyyy-MM-dd" // 2021-07-10
  | "yy" // 21
  | "MMMM" // July
  | "MMMM d" // July 10
  | "MMM" // Jul
  | "MMM d" // Jul 10
  | "MMM yyyy" // Jul 2025
  | "MMM d, HH:mm" // Jul 10, 14:30
  | "MMMM yyyy" // July 2025
  | "dd" // 10

export const formatDate = (date: string | number | Date, formatStr: DateFormat = "P") => {
  return format(date, formatStr, {
    locale: locales[i18next.language as keyof typeof locales],
  })
}

export const parseDate = (dateStr: string, formatStr: DateFormat = "P", baseDate = new Date()) => {
  return parse(dateStr, formatStr, baseDate, {
    locale: locales[i18next.language as keyof typeof locales],
  })
}

export const formatDistanceLang = (
  date: string | number | Date,
  baseDate: string | number | Date,
) => {
  return formatDistance(date, baseDate, {
    locale: locales[i18next.language as keyof typeof locales],
  })
}

export const formatDistanceStrictLang = (
  date: string | number | Date,
  baseDate: string | number | Date,
) => {
  return formatDistanceStrict(date, baseDate, {
    locale: locales[i18next.language as keyof typeof locales],
  })
}

export const formatRelativeLang = (
  date: string | number | Date,
  baseDate: string | number | Date,
) => {
  return formatRelative(date, baseDate, {
    locale: locales[i18next.language as keyof typeof locales],
  })
}

export const formatDistanceToNowLang = (
  date: string | number | Date,
  options?: FormatDistanceToNowOptions,
) => {
  return formatDistanceToNow(date, {
    ...options,
    locale: locales[i18next.language as keyof typeof locales],
  })
}

export const getDatePeriod = (date?: Date) => {
  const actualDate = date || new Date()

  return {
    week: getWeek(actualDate, { weekStartsOn: 1 }),
    month: getMonth(actualDate) + 1,
    year: getYear(actualDate),
  }
}

export const getDateRangeFromDatePeriod = ({ year, week }: Record<"year" | "week", number>) => {
  let date = new Date(year, 0, 1)
  date = setWeek(date, week, { weekStartsOn: 1 })
  return [startOfWeek(date, { weekStartsOn: 1 }), endOfWeek(date, { weekStartsOn: 1 })] as const
}

export const calculateAge = (birthdate?: Date | string | null) => {
  if (!birthdate) return undefined
  const today = new Date()
  const birth = typeof birthdate === "string" ? new Date(birthdate) : birthdate

  const m = today.getMonth() - birth.getMonth()
  let years = today.getFullYear() - birth.getFullYear()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) years--
  return Math.abs(years)
}

export const startOfDay = (date: Date) => {
  return startOfDayDateFns(date)
}

export const endOfDay = (date: Date) => {
  return endOfDayDateFns(date)
}

export const isSameDay = (date1: Date, date2: Date) => {
  return isSameDayDateFns(date1, date2)
}

export const weekStart = (date: Date) => {
  return startOfWeek(date, { weekStartsOn: 1 })
}

export const weekEnd = (date: Date) => {
  return endOfWeek(date, { weekStartsOn: 1 })
}

export { subDays, subMonths, subWeeks } from "date-fns"
